import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as sup from 'superstruct';

import { queryKeys } from '@/modules/api/queryKeys';
import { useSessionContext } from '@/modules/auth/hooks/useSessionContext';
import { useUpdateDemoNegotiationMutation } from '@/modules/demoNegotiation/hooks/useUpdateDemoNegotiationMutation';
import { Form } from '@/modules/form/components/Form/Form';
import { TextInput } from '@/modules/form/components/TextInput/TextInput';
import { isNotOnlyWhitespace, notEmpty } from '@/modules/form/utils/structs';
import { NegotiationTermRole } from '@/modules/negotiation/types/negotiationTypes';
import { formatPercentage } from '@/modules/negotiation/utils/formatPercentage';
import { Button } from '@/modules/theme/components/Button/Button';
import { Heading, LabelStyled } from '@/modules/theme/components/Typography';
import { FRACTIONS_PER_CURRENCY } from '@/utils/consts';
import { financialCompactFormatter } from '@/utils/financialCompactFormatter';
import { noop } from '@/utils/noop';
import { recompose } from '@/utils/structs/recompose';

import {
  ButtonWrapperStyled,
  DialogStyled,
  EstimationTextsWrapperStyled,
  FormColStyled,
  FormRowStyled,
  FormWrapperStyled,
  HorizontalLineStyled,
  TextRowStyled,
} from './DemoNegotiationDialog.styled';

export type DemoNegotiationPayload = {
  title: string;
  totalSpend: number;
  suppliersCount: number;
};

type DemoNegotiationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  negotiationId: string;
  estimatedDiscount: number;
  estimatedInflation: number;
  termRoles: NegotiationTermRole[];
  demoNegotiationPayload: DemoNegotiationPayload;
};

const maxNumber = 2_000_000_000;
const oneMillionDivider = 1_000_000;

const demoNegotiationStruct = sup.object({
  title: recompose(sup.string(), notEmpty, isNotOnlyWhitespace),
  totalSpend: sup.max(sup.number(), maxNumber / oneMillionDivider),
  suppliersCount: sup.max(sup.number(), maxNumber),
});

export const DemoNegotiationDialog = ({
  isOpen,
  negotiationId,
  estimatedDiscount,
  estimatedInflation,
  termRoles,
  demoNegotiationPayload,
  onClose,
}: DemoNegotiationDialogProps) => {
  const { t } = useTranslation('pages/SuggestedNegotiationsPage');
  const updateDemoNegotiationMutation = useUpdateDemoNegotiationMutation();
  const queryClient = useQueryClient();
  const {
    customer: { currency },
  } = useSessionContext();

  const errorMessages = {
    title: {
      'string.notEmpty': t('Value is required'),
      'string.isNotOnlyWhitespace': t(
        t('Value should not be composed only of spaces')
      ),
    },
    totalSpend: {
      number: t('Value must be a number'),
      'number.max': t("Spend can't be bigger than {{maxNumber}}", {
        maxNumber: maxNumber / oneMillionDivider,
      }),
    },
    suppliersCount: {
      number: t('Value must be a number'),
      'number.max': t("No. of suppliers can't be bigger than {{maxNumber}}", {
        maxNumber,
      }),
    },
  };

  const onValidSubmit = async (data: DemoNegotiationPayload) => {
    try {
      await updateDemoNegotiationMutation.mutateAsync({
        negotiationId,
        ...data,
        totalSpend:
          data.totalSpend * FRACTIONS_PER_CURRENCY * oneMillionDivider,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.customer.getDemoMetrics],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.customer.getDemoNegotiations],
        }),
      ]);
      onClose();
    } catch (error) {
      noop();
    }
  };

  const hasCostAvoidance = termRoles.includes(NegotiationTermRole.LOCK_PRICE);
  const hasEstimatedDiscount = termRoles.includes(
    NegotiationTermRole.OFFER_DISCOUNT
  );

  return (
    <DialogStyled onClose={onClose} isOpen={isOpen} hasCloseIcon>
      <FormWrapperStyled>
        <Form
          onValidSubmit={onValidSubmit}
          struct={demoNegotiationStruct}
          defaultValues={{
            ...demoNegotiationPayload,
            totalSpend: demoNegotiationPayload.totalSpend / oneMillionDivider,
          }}
        >
          {({ register, watch }) => {
            const totalSpendDiscount =
              watch('totalSpend') * estimatedDiscount * oneMillionDivider;
            const totalSpendInflation =
              watch('totalSpend') * estimatedInflation * oneMillionDivider;
            return (
              <>
                <div>
                  <FormRowStyled>
                    <FormColStyled>
                      <LabelStyled>{t('Name of negotiation')}</LabelStyled>
                    </FormColStyled>
                    <FormColStyled>
                      <TextInput
                        {...register('title')}
                        errorMessages={errorMessages.title}
                      />
                    </FormColStyled>
                  </FormRowStyled>
                  <FormRowStyled>
                    <FormColStyled>
                      <LabelStyled>
                        {t('Annual spend (M{{currency}})', {
                          currency: currency,
                        })}
                      </LabelStyled>
                    </FormColStyled>
                    <FormColStyled>
                      <TextInput
                        {...register('totalSpend', { valueAsNumber: true })}
                        max={maxNumber}
                        errorMessages={errorMessages.totalSpend}
                      />
                    </FormColStyled>
                  </FormRowStyled>
                  <FormRowStyled>
                    <FormColStyled>
                      <LabelStyled>
                        {t('No. of suppliers {{amount}}', {
                          amount: '',
                        })}
                      </LabelStyled>
                    </FormColStyled>
                    <FormColStyled>
                      <TextInput
                        {...register('suppliersCount', { valueAsNumber: true })}
                        max={maxNumber}
                        errorMessages={errorMessages.suppliersCount}
                      />
                    </FormColStyled>
                  </FormRowStyled>
                </div>

                <HorizontalLineStyled />

                <EstimationTextsWrapperStyled>
                  {hasEstimatedDiscount && (
                    <TextRowStyled>
                      <Heading variant="h3">{t('Estimated savings')}</Heading>
                      <Heading variant="h3">
                        {financialCompactFormatter.format(
                          isNaN(totalSpendDiscount) ? 0 : totalSpendDiscount
                        )}{' '}
                        ({formatPercentage(estimatedDiscount) * 100}%)
                      </Heading>
                    </TextRowStyled>
                  )}
                  {hasCostAvoidance && (
                    <TextRowStyled>
                      <Heading variant="h3">
                        {t('Estimated cost avoidance')}
                      </Heading>
                      <Heading variant="h3">
                        {financialCompactFormatter.format(
                          isNaN(totalSpendInflation) ? 0 : totalSpendInflation
                        )}{' '}
                        ({formatPercentage(estimatedInflation) * 100}%)
                      </Heading>
                    </TextRowStyled>
                  )}
                </EstimationTextsWrapperStyled>

                <ButtonWrapperStyled>
                  <Button
                    type="submit"
                    loading={updateDemoNegotiationMutation.isPending}
                  >
                    {t('Confirm')}
                  </Button>
                </ButtonWrapperStyled>
              </>
            );
          }}
        </Form>
      </FormWrapperStyled>
    </DialogStyled>
  );
};
